import { useMemo, useState } from "react";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";

/**
 * Reusable client-side data table with search, select filters, column sorting and pagination.
 * columns: [{ key, label, sortable?, render?(row), sortValue?(row) }]
 * filters: [{ key, label, options: [{value,label}] , match?(row, value) }]
 * searchKeys: string[] of row fields to full-text search
 */
export default function DataTable({ columns, rows, filters = [], searchKeys = [], pageSize = 10, testId = "data-table", emptyText = "No records found.", initialFilters = {} }) {
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState(initialFilters);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let out = rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((r) => searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(q)));
    }
    for (const f of filters) {
      const v = filterValues[f.key];
      if (v) out = out.filter((r) => (f.match ? f.match(r, v) : String(r[f.key] ?? "") === v));
    }
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      out = [...out].sort((a, b) => {
        const av = col?.sortValue ? col.sortValue(a) : a[sortKey];
        const bv = col?.sortValue ? col.sortValue(b) : b[sortKey];
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * sortDir;
        return String(av ?? "").localeCompare(String(bv ?? "")) * sortDir;
      });
    }
    return out;
  }, [rows, search, filterValues, sortKey, sortDir, filters, searchKeys, columns]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => -d);
    else { setSortKey(key); setSortDir(1); }
  };

  const setFilter = (key, v) => { setFilterValues((f) => ({ ...f, [key]: v })); setPage(1); };

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid={`${testId}-search`} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search…" className="border bg-white pl-8 pr-3 py-2 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-[#E05A47]/60" />
        </div>
        {filters.map((f) => (
          <select key={f.key} data-testid={`${testId}-filter-${f.key}`} value={filterValues[f.key] || ""}
            onChange={(e) => setFilter(f.key, e.target.value)}
            className="border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E05A47]/60">
            <option value="">{f.label}: All</option>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ))}
        <span className="text-xs text-muted-foreground ml-auto" data-testid={`${testId}-count`}>
          {filtered.length} record{filtered.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="border bg-white rounded-xl mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-[#F2F5F1] text-left">
              {columns.map((c) => (
                <th key={c.key} className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-bold whitespace-nowrap">
                  {c.sortable ? (
                    <button data-testid={`${testId}-sort-${c.key}`} onClick={() => toggleSort(c.key)}
                      className="inline-flex items-center gap-1 uppercase tracking-[0.14em] font-bold hover:text-[#0A251D] transition-colors duration-100">
                      {c.label}
                      {sortKey === c.key ? (sortDir === 1 ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : <ChevronDown size={12} className="opacity-25" />}
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={r.id || i} className="border-b last:border-0 hover:bg-[#F5F8F4] transition-colors duration-100">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3 align-middle">{c.render ? c.render(r) : String(r[c.key] ?? "—")}</td>
                ))}
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">{emptyText}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3">
          <button data-testid={`${testId}-prev`} disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}
            className="border bg-white p-1.5 rounded-lg disabled:opacity-40 hover:border-[#0A251D] transition-colors duration-100"><ChevronLeft size={14} /></button>
          <span className="text-xs text-muted-foreground">Page {safePage} of {pages}</span>
          <button data-testid={`${testId}-next`} disabled={safePage >= pages} onClick={() => setPage(safePage + 1)}
            className="border bg-white p-1.5 rounded-lg disabled:opacity-40 hover:border-[#0A251D] transition-colors duration-100"><ChevronRight size={14} /></button>
        </div>
      )}
    </div>
  );
}
